import type { CSSProperties } from "react";

// The client login page shows the banner in a panel that is close to 5:4
export const BANNER_ASPECT_RATIO = 5 / 4;

const ACCEPTED_TYPES = ["image/jpeg", "image/png", "image/webp"];
const MAX_UPLOAD_BYTES = 10 * 1024 * 1024;
const MIN_UPLOAD_WIDTH = 1600;
const MIN_UPLOAD_HEIGHT = MIN_UPLOAD_WIDTH / BANNER_ASPECT_RATIO;
// The saved banner is never narrower or wider than this
const MIN_OUTPUT_WIDTH = 1280;
const MAX_OUTPUT_WIDTH = 2560;
const MAX_ZOOM = 3;

/**
 * Position of an image inside the 5:4 frame. `x` and `y` are the point of the
 * image at the centre of the frame, as 0-1 fractions of the image. At zoom 1
 * the image just covers the frame.
 */
export type BannerCrop = { x: number; y: number; zoom: number };

export const DEFAULT_BANNER_CROP: BannerCrop = { x: 0.5, y: 0.5, zoom: 1 };

export type BannerUpload = {
  file: File;
  width: number;
  height: number;
};

/** A banner as displayed: a ready 5:4 image, or an upload with its pending crop */
export type BannerView = {
  url: string;
  framing?: { crop: BannerCrop; imageAspectRatio: number };
};

/** Fraction of the image that is visible in the frame along each axis */
function visibleFraction(imageAspectRatio: number, zoom: number) {
  return {
    width: Math.min(1, BANNER_ASPECT_RATIO / imageAspectRatio) / zoom,
    height: Math.min(1, imageAspectRatio / BANNER_ASPECT_RATIO) / zoom,
  };
}

const clamp = (value: number, min: number, max: number) =>
  Math.min(Math.max(value, min), max);

/** Keeps the frame inside the image */
export function clampCrop(
  crop: BannerCrop,
  imageAspectRatio: number
): BannerCrop {
  const visible = visibleFraction(imageAspectRatio, crop.zoom);
  return {
    zoom: crop.zoom,
    x: clamp(crop.x, visible.width / 2, 1 - visible.width / 2),
    y: clamp(crop.y, visible.height / 2, 1 - visible.height / 2),
  };
}

/** Moves the image by `dx` and `dy`, given as fractions of the frame size */
export function panCrop(
  crop: BannerCrop,
  imageAspectRatio: number,
  dx: number,
  dy: number
): BannerCrop {
  const visible = visibleFraction(imageAspectRatio, crop.zoom);
  return clampCrop(
    {
      zoom: crop.zoom,
      x: crop.x - dx * visible.width,
      y: crop.y - dy * visible.height,
    },
    imageAspectRatio
  );
}

/** Styles for an absolutely positioned image inside a 5:4 frame */
export function getCropStyle(
  crop: BannerCrop,
  imageAspectRatio: number
): CSSProperties {
  const visible = visibleFraction(imageAspectRatio, crop.zoom);
  return {
    width: `${100 / visible.width}%`,
    height: `${100 / visible.height}%`,
    left: `${(-(crop.x - visible.width / 2) / visible.width) * 100}%`,
    top: `${(-(crop.y - visible.height / 2) / visible.height) * 100}%`,
  };
}

/** Zooming in further would save a banner narrower than MIN_OUTPUT_WIDTH */
export function getMaxZoom({ width, height }: BannerUpload) {
  const frameWidth = Math.min(width, height * BANNER_ASPECT_RATIO);
  return clamp(frameWidth / MIN_OUTPUT_WIDTH, 1, MAX_ZOOM);
}

/** Decodes a file through an <img>, which applies the photo's EXIF rotation */
async function loadImage(file: File) {
  const url = URL.createObjectURL(file);
  try {
    return await new Promise<HTMLImageElement>((resolve, reject) => {
      const image = new Image();
      image.onload = () => resolve(image);
      image.onerror = () => reject(new Error("This image could not be read"));
      image.src = url;
    });
  } finally {
    URL.revokeObjectURL(url);
  }
}

/** Validates a picked file. Rejects with a message that can be shown to the user. */
export async function loadBannerUpload(file: File): Promise<BannerUpload> {
  if (!ACCEPTED_TYPES.includes(file.type)) {
    throw new Error("Upload a JPEG, PNG or WebP image");
  }
  if (file.size > MAX_UPLOAD_BYTES) {
    throw new Error("Image must be smaller than 10 MB");
  }

  const { naturalWidth: width, naturalHeight: height } = await loadImage(file);
  if (width < MIN_UPLOAD_WIDTH || height < MIN_UPLOAD_HEIGHT) {
    throw new Error(
      `Image must be at least ${MIN_UPLOAD_WIDTH} x ${MIN_UPLOAD_HEIGHT} px`
    );
  }
  return { file, width, height };
}

function canvasToBlob(
  canvas: HTMLCanvasElement,
  type: string,
  quality: number
) {
  return new Promise<Blob | null>((resolve) => {
    canvas.toBlob(resolve, type, quality);
  });
}

/** Renders the framed part of the upload as a 5:4 image file */
export async function exportCroppedBanner(
  upload: BannerUpload,
  crop: BannerCrop
): Promise<File> {
  const image = await loadImage(upload.file);
  const visible = visibleFraction(upload.width / upload.height, crop.zoom);
  const sourceWidth = visible.width * upload.width;
  const sourceHeight = visible.height * upload.height;

  const canvas = document.createElement("canvas");
  canvas.width = Math.round(Math.min(sourceWidth, MAX_OUTPUT_WIDTH));
  canvas.height = Math.round(canvas.width / BANNER_ASPECT_RATIO);
  const context = canvas.getContext("2d");
  if (!context) throw new Error("This image could not be processed");

  context.drawImage(
    image,
    (crop.x - visible.width / 2) * upload.width,
    (crop.y - visible.height / 2) * upload.height,
    sourceWidth,
    sourceHeight,
    0,
    0,
    canvas.width,
    canvas.height
  );

  // Safari can't encode WebP and silently returns a PNG instead
  let blob = await canvasToBlob(canvas, "image/webp", 0.85);
  if (blob?.type !== "image/webp") {
    blob = await canvasToBlob(canvas, "image/jpeg", 0.9);
  }
  if (!blob) throw new Error("This image could not be processed");

  const extension = blob.type === "image/webp" ? "webp" : "jpg";
  return new File([blob], `login-banner.${extension}`, { type: blob.type });
}
